import {
  Button,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { Alert } from "@material-ui/lab";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import { useAuthUser } from "../../../../../../reducers/hooks/useAuthUser";
import Pagination from "../../../../components/Pagination/Pagination";
import { useCandidateAttachments } from "../../api/getCandidateAttachments";
import { TCandidateAttachment } from "../../types/candidateAttachment.types";
import {
  formatFileSize,
  getFileExtension,
} from "../../util/candidateAttachment.util";
import CandidateAttachmentViewerDialog from "../CandidateAttachmentViewerDialog/CandidateAttachmentViewerDialog";
import DeleteCandidateAttachmentButton from "../DeleteCandidateAttachmentButton/DeleteCandidateAttachmentButton";
import UploadCandidateAttachmentsDialog from "../UploadCandidateAttachmentsDialog/UploadCandidateAttachmentsDialog";

const StyledWrapper = styled.div`
  padding: 1rem;
`;

const StyledToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

const StyledAddButton = styled(Button)`
  text-transform: none;
`;

const StyledTable = styled(Table)`
  background-color: ${cuideoTheme.palette.common.white};

  .MuiTableCell-root {
    font-size: 0.875rem;
    color: ${cuideoTheme.palette.primary.main};
  }
  .MuiTableCell-head {
    font-weight: 700;
  }
  .MuiLink-button {
    font-size: 0.875rem;
    text-align: left;
    word-break: break-word;
  }
`;

const StyledTypeCell = styled(TableCell)`
  word-break: break-all;
`;

const StyledCenter = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 0;
`;

const StyledEmptyText = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 1rem;
  text-align: center;
  padding: 2rem 0;
`;

const StyledPaginationWrapper = styled.div`
  margin-top: 1rem;
`;

const ITEMS_PER_PAGE = 10;
const DATE_FORMAT = "DD/MM/YYYY HH:mm";
const DEFAULT_TITLE = "documento";

const attachmentType = (attachment: TCandidateAttachment): string => {
  if (attachment.mimeType) {
    return attachment.mimeType;
  }

  const extension = getFileExtension(attachment.title ?? "");

  return extension !== "" ? extension.toUpperCase() : "-";
};

interface ICandidateAttachmentsTabProps {
  candidate: {
    id: string;
  };
}

/**
 * ECO-1416: «Documentos» tab of the candidate profile. Lists the candidate's
 * attachments, newest first and 10 per page, and lets them be uploaded,
 * previewed, downloaded and (promote managers only) deleted.
 */
const CandidateAttachmentsTab = (props: ICandidateAttachmentsTabProps) => {
  const { candidate } = props;

  const { permissions } = useAuthUser();
  const [page, setPage] = useState(1);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [viewedAttachment, setViewedAttachment] =
    useState<TCandidateAttachment | null>(null);

  const attachmentsQuery = useCandidateAttachments({
    params: { candidateId: candidate.id, page },
  });

  const attachments = attachmentsQuery.data?.["hydra:member"] ?? [];
  const totalItems = attachmentsQuery.data?.["hydra:totalItems"] ?? 0;
  const numPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // Deleting the last document of the last page leaves that page empty
  useEffect(() => {
    if (numPages > 0 && page > numPages) {
      setPage(numPages);
    }
  }, [numPages, page]);

  const handleAddClick = () => {
    setIsUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setIsUploadDialogOpen(false);
  };

  // The newest documents are on the first page
  const handleUploaded = () => {
    setPage(1);
  };

  const handleViewerClose = () => {
    setViewedAttachment(null);
  };

  const renderTable = () => {
    if (attachmentsQuery.isLoading) {
      return (
        <StyledCenter>
          <CircularProgress />
        </StyledCenter>
      );
    }
    if (attachmentsQuery.isError) {
      return (
        <Alert severity="error">
          <FormattedMessage
            id="CandidateAttachmentsTab.No se han podido cargar los documentos"
            defaultMessage="No se han podido cargar los documentos"
          />
        </Alert>
      );
    }
    if (attachments.length === 0) {
      return (
        <StyledEmptyText>
          <FormattedMessage
            id="CandidateAttachmentsTab.Esta candidata no tiene documentos"
            defaultMessage="Esta candidata no tiene documentos"
          />
        </StyledEmptyText>
      );
    }

    return (
      <StyledTable size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <FormattedMessage
                id="CandidateAttachmentsTab.Título"
                defaultMessage="Título"
              />
            </TableCell>
            <TableCell>
              <FormattedMessage
                id="CandidateAttachmentsTab.Fecha de subida"
                defaultMessage="Fecha de subida"
              />
            </TableCell>
            <TableCell>
              <FormattedMessage
                id="CandidateAttachmentsTab.Subido por"
                defaultMessage="Subido por"
              />
            </TableCell>
            <TableCell>
              <FormattedMessage
                id="CandidateAttachmentsTab.Tamaño"
                defaultMessage="Tamaño"
              />
            </TableCell>
            <TableCell>
              <FormattedMessage
                id="CandidateAttachmentsTab.Tipo"
                defaultMessage="Tipo"
              />
            </TableCell>
            {permissions.canDeleteCandidateAttachments && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {attachments.map((attachment) => (
            <TableRow key={attachment.id}>
              <TableCell>
                <Link
                  component="button"
                  onClick={() => {
                    setViewedAttachment(attachment);
                  }}
                >
                  {attachment.title || DEFAULT_TITLE}
                </Link>
              </TableCell>
              <TableCell>
                {moment(attachment.createdAt).format(DATE_FORMAT)}
              </TableCell>
              <TableCell>{attachment.createdUser?.name || "-"}</TableCell>
              <TableCell>{formatFileSize(attachment.sizeBytes)}</TableCell>
              <StyledTypeCell>{attachmentType(attachment)}</StyledTypeCell>
              {permissions.canDeleteCandidateAttachments && (
                <TableCell align="right">
                  <DeleteCandidateAttachmentButton
                    candidateId={candidate.id}
                    attachment={attachment}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
    );
  };

  return (
    <StyledWrapper className="MuiCuideo-CandidateAttachmentsTab">
      <StyledToolbar>
        <StyledAddButton
          variant="contained"
          color="primary"
          startIcon={<AttachFileIcon />}
          onClick={handleAddClick}
        >
          <FormattedMessage
            id="CandidateAttachmentsTab.Añadir documento"
            defaultMessage="Añadir documento"
          />
        </StyledAddButton>
      </StyledToolbar>
      {renderTable()}
      {numPages > 1 && (
        <StyledPaginationWrapper>
          <Pagination count={numPages} page={page} onChange={setPage} />
        </StyledPaginationWrapper>
      )}
      <UploadCandidateAttachmentsDialog
        open={isUploadDialogOpen}
        candidateId={candidate.id}
        onClose={handleUploadDialogClose}
        onUploaded={handleUploaded}
      />
      <CandidateAttachmentViewerDialog
        open={viewedAttachment !== null}
        candidateId={candidate.id}
        attachment={viewedAttachment}
        onClose={handleViewerClose}
      />
    </StyledWrapper>
  );
};

export default CandidateAttachmentsTab;
